import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SvgIcon } from "../Components/SvgIcon";
import { RiArrowDropDownLine } from "react-icons/ri";
import { useGlobal } from "../context/GlobalContext";

const listVariants = {
  hidden: { opacity: 0, height: 0 },
  show: {
    opacity: 1,
    height: "auto",
    transition: { duration: 0.3, ease: "easeOut", staggerChildren: 0.04 },
  },
  exit: { opacity: 0, height: 0, transition: { duration: 0.2, ease: "easeIn" } },
};

const itemVariants = {
  hidden: { opacity: 0, x: -12 },
  show: { opacity: 1, x: 0, transition: { duration: 0.25 } },
};

function ItemContent({ item }) {
  return (
    <>
      <div className="flex items-center gap-3 min-w-0">
        <span className="shrink-0 transition-transform duration-300 group-hover:scale-110">
          <SvgIcon name={item.type === "video" ? "navItemVideo" : "questionMark"} />
        </span>
        <div className="text-[11px] md:text-[12px] font-light md:font-normal text-white leading-normal truncate lg:whitespace-normal">
          {item.title}
        </div>
      </div>
      <div className="shrink-0 flex items-center">
        {item.status === "locked" && <SvgIcon name={"lessonLock"} />}
        {item.status === "unlocked" && (
          <motion.span
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          >
            <SvgIcon name="sphere" />
          </motion.span>
        )}
        {item.status === "completed" && (
          <motion.span
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
          >
            <SvgIcon name={"tickMark"} />
          </motion.span>
        )}
      </div>
    </>
  );
}

export default function CourseNavItem({
  section,
  courseModules,
  colorStyle,
  isOpen,
  onClick,
}) {
  const { userProgress } = useGlobal();
  const [selectedItem, setSelectedItem] = useState(userProgress?.currentItem?.id);
  const [selectedModuleId, setSelectedModuleId] = useState(
    userProgress?.currentModule?.id
  );

  const handleModuleSelect = (id) =>
    setSelectedModuleId(selectedModuleId === id ? null : id);

  const handleNavItemClick = (id) => setSelectedItem(id);

  const handleKeyActivate = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };

  const { total, done, percent } = useMemo(() => {
    const all = courseModules?.flatMap((m) => m.content || []) || [];
    const completed = all.filter((i) => i.status === "completed").length;
    return {
      total: all.length,
      done: completed,
      percent: all.length ? Math.round((completed / all.length) * 100) : 0,
    };
  }, [courseModules]);

  const levelBadge = (
    <div
      className="flex items-center py-[3px] px-[10px] rounded-[45px] bg-[#4a5065] font-bold uppercase text-xs tracking-wider leading-normal text-custom-color border-[1px] border-custom-color shadow-[0_0_14px_-4px_var(--level-color)]"
      style={{ "--level-color": colorStyle }}
    >
      {section?.levelName}
    </div>
  );

  return (
    <motion.div layout transition={{ duration: 0.3 }}>
      {isOpen ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden  flex flex-col gap-4 rounded-[9.83px] bg-[#ffffff1a] backdrop-blur-md border-[1px] border-custom-color px-5 py-6 shadow-[0_10px_40px_-10px_var(--level-color)]"
          style={{ "--level-color": colorStyle }}
        >
          {/* soft glow */}
          <div
            className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full opacity-20 blur-3xl"
            style={{ backgroundColor: colorStyle }}
          />

          <div
            role="button"
            tabIndex={0}
            aria-expanded={true}
            onKeyDown={handleKeyActivate}
            className="relative flex flex-col gap-3 cursor-pointer group focus-visible:outline-none"
            onClick={onClick}
          >
            <div className="w-full flex justify-between items-center">
              {levelBadge}
              <motion.span className="transition-all" whileHover={{ y: 2 }}>
                <SvgIcon name={"vectorDown"} />
              </motion.span>
            </div>
            <h3 className="text-left text-white font-poppins leading-[22.4px] font-bold text-[16px]">
              {section?.title}
            </h3>
            <p className="text-left text-[#A0AEC0] text-[12px] leading-relaxed font-normal">
              {section?.description}
            </p>

            {total > 0 && (
              <div className="flex flex-col gap-1.5 mt-1">
                <div className="flex justify-between text-[11px] text-[#A0AEC0]">
                  <span>
                    {done}/{total} completed
                  </span>
                  <span>{percent}%</span>
                </div>
                <div className="h-[4px] w-full rounded-full bg-[#4a5065] overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundColor: colorStyle }}
                    initial={{ width: 0 }}
                    animate={{ width: `${percent}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* modules ("Pilier" rows) */}
          <div className="relative w-full flex flex-col gap-2">
            {courseModules?.map((module) => {
              const moduleOpen = selectedModuleId === module.id;
              const lessons = module?.content || [];
              const mTotal = lessons.length;
              const mDone = lessons.filter((i) => i.status === "completed").length;
              const mPercent = mTotal ? Math.round((mDone / mTotal) * 100) : 0;
              const allDone = mTotal > 0 && mDone === mTotal;

              return (
                <div key={module.id} className="flex flex-col">
                  <motion.div
                    role="button"
                    tabIndex={0}
                    aria-expanded={moduleOpen}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleModuleSelect(module.id);
                      }
                    }}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 320, damping: 22 }}
                    className={`group relative overflow-hidden flex items-center justify-between gap-3 cursor-pointer rounded-[8px] bg-[#4a5065] border-[1px] px-3 py-3 transition-all duration-300 hover:brightness-110 hover:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--level-color)] ${moduleOpen ? "border-custom-color" : "border-white/5"
                      }`}
                    style={{ "--level-color": colorStyle }}
                    onClick={() => handleModuleSelect(module.id)}
                  >
                    {/* level-color accent bar */}
                    <motion.span
                      className="absolute left-0 top-0 h-full w-[3px] origin-center"
                      style={{ backgroundColor: colorStyle }}
                      initial={false}
                      animate={{
                        scaleY: moduleOpen ? 1 : 0,
                        opacity: moduleOpen ? 1 : 0,
                      }}
                      transition={{ duration: 0.25 }}
                    />

                    <div className="flex min-w-0 flex-col gap-1.5">
                      <span className="text-white text-left text-[12px] font-semibold leading-snug">
                        {module.title}
                      </span>
                      {mTotal > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="h-[3px] w-14 rounded-full bg-[#13182A] overflow-hidden">
                            <motion.div
                              className="h-full rounded-full"
                              style={{ backgroundColor: colorStyle }}
                              initial={{ width: 0 }}
                              animate={{ width: `${mPercent}%` }}
                              transition={{ duration: 0.7, ease: "easeOut" }}
                            />
                          </div>
                          <span className="text-[10px] text-[#A0AEC0]">
                            {mDone}/{mTotal}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      {allDone && <SvgIcon name={"tickMark"} />}
                      <motion.span
                        animate={{ rotate: moduleOpen ? 180 : 0 }}
                        transition={{ duration: 0.25 }}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-[#13182A] transition-shadow duration-300 group-hover:shadow-[0_0_14px_-2px_var(--level-color)]"
                      >
                        <RiArrowDropDownLine className="text-white h-6 w-6" />
                      </motion.span>
                    </div>
                  </motion.div>

                  <AnimatePresence initial={false}>
                    {moduleOpen && (
                      <motion.ul
                        key="list"
                        variants={listVariants}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                        className="flex flex-col gap-[8px] w-full overflow-hidden pt-2"
                      >
                        {module?.content?.length > 0 &&
                          module.content.map((item) => {
                            const locked = item.status === "locked";
                            const active = selectedItem === item?.id && !locked;
                            return (
                              <motion.li
                                key={item.id}
                                variants={itemVariants}
                                whileHover={locked ? {} : { x: 4 }}
                                className={`group relative overflow-hidden text-[#A0AEC0] font-poppins text-left text-[14px] font-normal leading-normal rounded-[5px] transition-colors duration-300
                                  ${active ? "bg-[#000000]" : "bg-[#13182A]"}
                                  ${locked ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                                onClick={() =>
                                  handleNavItemClick(locked ? selectedItem : item.id)
                                }
                              >
                                {active && (
                                  <motion.span
                                    layoutId={`active-${section?.title}`}
                                    className="absolute left-0 top-0 h-full w-[3px]"
                                    style={{ backgroundColor: colorStyle }}
                                  />
                                )}

                                {locked ? (
                                  <div className="flex justify-between items-center gap-[6px] w-full p-[10px]">
                                    <ItemContent item={item} />
                                  </div>
                                ) : (
                                  <Link
                                    to={`/resume/course-content/items/${item.id}`}
                                    state={{
                                      sectionName: section?.title,
                                      moduleName: module.title,
                                      id: item.id,
                                    }}
                                    className="flex justify-between items-center gap-[6px] w-full p-[10px]"
                                  >
                                    <ItemContent item={item} />
                                  </Link>
                                )}
                              </motion.li>
                            );
                          })}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </motion.div>
      ) : (
        <motion.div
          role="button"
          tabIndex={0}
          aria-expanded={false}
          onKeyDown={handleKeyActivate}
          onClick={onClick}
          whileHover={{ y: -4 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", stiffness: 300, damping: 22 }}
          className="group relative overflow-hidden flex flex-col gap-4 min-h-[190px] rounded-[9.83px] bg-[linear-gradient(135deg,rgba(10,20,80,0.75)_0%,rgba(6,12,50,0.85)_55%,rgba(5,9,38,0.92)_100%)] backdrop-blur-md border-[1px] border-custom-color p-5 cursor-pointer shadow-[0_6px_24px_-12px_rgba(0,0,0,0.6)] hover:shadow-[0_14px_40px_-12px_var(--level-color)] transition-shadow duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--level-color)]"
          style={{ "--level-color": colorStyle }}
        >
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-[2px] opacity-80"
            style={{
              background: `linear-gradient(90deg, ${colorStyle}, transparent)`,
            }}
          />
          <div
            className="pointer-events-none absolute  -top-16 -right-16 h-40 w-40 rounded-full opacity-10 group-hover:opacity-25 blur-3xl transition-opacity duration-500"
            style={{ backgroundColor: colorStyle }}
          />

          <div className="relative flex items-center justify-between gap-2">
            {levelBadge}
            {total > 0 && (
              <span className="shrink-0 rounded-full bg-[#13182A] px-2.5 py-[3px] text-[11px] font-medium text-[#A0AEC0]">
                {total} {total === 1 ? "lesson" : "lessons"}
              </span>
            )}
          </div>

          <div className="relative flex flex-col gap-2">
            <h3 className="text-left text-white font-poppins leading-[22.4px] font-bold text-[16px] line-clamp-2">
              {section?.title}
            </h3>
            <p className="text-left text-[#A0AEC0] text-[12px] leading-relaxed font-normal line-clamp-2">
              {section?.description}
            </p>
          </div>

          <div className="relative mt-auto flex items-end justify-between gap-4">
            <div className="flex flex-1 flex-col gap-1.5">
              <div className="flex justify-between text-[11px] text-[#A0AEC0]">
                <span>{total > 0 ? `${done}/${total} completed` : "Not started"}</span>
                <span>{percent}%</span>
              </div>
              <div className="h-[4px] w-full rounded-full bg-[#fff] overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: colorStyle }}
                  initial={{ width: 0 }}
                  animate={{ width: `${percent}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              </div>
            </div>

            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#13182A] border-[1px] border-custom-color transition-all duration-300 group-hover:translate-x-0.5 group-hover:shadow-[0_0_16px_-2px_var(--level-color)]"
              style={{ "--level-color": colorStyle }}
            >
              <SvgIcon name={"vectorRight"} />
            </span>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}